import React, { Component, createRef, ReactElement } from 'react';

import debounce from 'lodash.debounce';
import InfiniteScroll from 'react-infinite-scroller';

import { AutocompleteAPI } from '@philips-emr/tws-components-api';
import { Alignment, Breakpoint, ModalType } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider, Value } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, expression, number, object, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { ChangeEvent, FocusEvent, PageEvent, SelectEvent } from '@philips-emr/tws-core/lib/component/types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, defaultClasses, getBreakpoint, isDefined, isEqualShallow, isFunction, isMobile, isObject } from '@philips-emr/tws-utils';

import { Input, Modal, Popover, SelectableIterator } from '..';

import { FakeInput, ModalInput } from './components';

import './autocomplete.css';

export interface AutocompleteProps {
  children: ReactElement | ReactElement[];
  className?: string;
  data: Record<string, Value>[];
  done?: boolean;
  emptyResultExpression?: string;
  enabled?: boolean;
  entry: string;
  limit: number;
  loading?: boolean;
  localData: boolean;
  minSearchLength: number;
  onBlur?: (event: FocusEvent) => void;
  onChange?: (event: ChangeEvent<Record<string, Value>>) => void;
  onFocus?: (event: FocusEvent) => void;
  onNextPage?: (event: PageEvent) => void;
  placeholder?: string;
  primaryKey: string;
  queryKey: string;
  readOnly?: boolean;
  title?: string;
  uuid?: string;
  value?: Record<string, Value>;
}

export interface AutocompleteState {
  focused: boolean;
  internalValue?: Record<string, Value> | null;
  open: boolean;
  query: string;
}

const acceptedTypes = [
  ComponentType.BEHAVIOR,
  ComponentType.LIST,
];

@AwesomeComponent({ type: ComponentType.DATA_ENTRY, api: AutocompleteAPI })
class Autocomplete extends Component<AutocompleteProps, AutocompleteState> {
  inputRef = createRef<Input>();
  modalRef = createRef<Modal>();

  state: AutocompleteState = {
    focused: false,
    open: false,
    query: '',
  };

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    data: arrayOf(object).notEnumerable(),
    done: bool.notEnumerable(),
    emptyResultExpression: expression,
    enabled: bool,
    entry: string,
    limit: number,
    loading: bool.notEnumerable(),
    localData: bool,
    minSearchLength: number,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    onNextPage: eventListener,
    placeholder: expression,
    primaryKey: string,
    queryKey: string,
    readOnly: bool,
    title: expression,
    uuid: string,
    value: object.notEnumerable(),
  };

  static defaultProps = {
    enabled: true,
    entry: '',
    limit: 12,
    localData: false,
    minSearchLength: 3,
    primaryKey: 'id',
    queryKey: 'query',
  };

  componentDidMount(): void {
    const { value } = this.props;

    if (value) {
      this.applyValueToState();
    }
  }

  componentDidUpdate(prevProps: AutocompleteProps): void {
    const { value } = this.props;
    const { value: oldValue } = prevProps;

    if (value === oldValue || (isEqualShallow(value, oldValue))) {
      return;
    }

    this.applyValueToState();
  }

  applyValueToState(): void {
    const { value } = this.props;

    if (value !== null && isDefined(value) && !isObject(value)) {
      throw new Error('[Autocomplete]: Trying to use a non-object value for the value property.');
    }

    this.setState({ internalValue: value });
  }

  onClearClick = (): void => {
    this.setState({ internalValue: null, query: '' }, this.triggerOnChange);
  };

  onClose = (): void => {
    this.setState({ open: false });
  };

  onInputBlur = (): void => {
    const { onBlur } = this.props;

    if (isFunction(onBlur)) {
      onBlur(new FocusEvent());
    }

    this.setState({ focused: false });
  };

  onInputChange = (event: ChangeEvent<string>): void => {
    const { value } = event;

    this.setState({ query: value }, this.$search);
  };

  onInputFocus = (search: boolean = true): void => {
    const { readOnly, onFocus } = this.props;

    if (readOnly) {
      isFunction(onFocus) && onFocus(new FocusEvent());
      return;
    }

    if (!this.shouldOpenOnFocus()) {
      return this.setState({ focused: true });
    }

    this.inputRef.current?.selectInput();

    this.setState({ open: true }, () => {
      if (search) {
        this.$search();
      }

      if(isFunction(onFocus)) {
        onFocus(new FocusEvent());
      }
    });
  };

  onItemSelected = (event: SelectEvent<Record<string, Value>>): void => {
    const { value } = event;

    this.setState({
      internalValue: value,
      open: false,
    }, this.triggerOnChange);
  };

  onLoadMore = (nextPage: number): void => {
    const { done, loading, onNextPage } = this.props;

    if (done || loading || !isFunction(onNextPage)) {
      return;
    }

    onNextPage(this.getPageEvent(nextPage, true));
  };

  onModalInputFocus = (): void => {
    this.$search();
  };

  search(): void {
    const { minSearchLength, onNextPage } = this.props;
    const { query } = this.state;

    if (query.length < Number(minSearchLength) || !isFunction(onNextPage)) {
      return;
    }

    this.setState({ open: true }, (): void => {
      onNextPage(this.getPageEvent(0, false));
    });
  }

  $search = debounce(this.search, 300);

  triggerOnChange(): void {
    const { onChange, value: oldValue } = this.props;
    const { internalValue } = this.state;

    if (isFunction(onChange)) {
      onChange(new ChangeEvent<Record<string, Value>>({ oldValue, value: internalValue }));
    }
  }

  shouldOpenOnFocus(): boolean {
    const { enabled, minSearchLength, readOnly } = this.props;
    const { query } = this.state;

    return (enabled || !readOnly) && (Number(minSearchLength) <= 0 || query.length >= Number(minSearchLength) || isMobile());
  }

  getClassName(): string {
    const { readOnly, enabled, className } = this.props;
    const { internalValue, open, query } = this.state;

    return classNames({
      'c-autocomplete': true,
      'has-value': Boolean(internalValue || query.length),
      'is-open': open,
      [`${className}`]: Boolean(className),
      ...defaultClasses({
        enabled: Boolean(enabled),
        readOnly: Boolean(readOnly),
      }),
    });
  }

  getDescriptionValue(): string {
    const { entry } = this.props;
    const { internalValue } = this.state;

    if (internalValue && entry in internalValue) {
      return msg(String(internalValue[entry]));
    }

    return '';
  }

  getLimit(): number {
    const { limit } = this.props;

    const breakpoint = getBreakpoint();

    if (breakpoint === Breakpoint.XSM) {
      return Math.floor(limit * 1.4);
    }

    if (breakpoint === Breakpoint.SM) {
      return Math.floor(limit * 2);
    }

    return limit;
  }

  getPageEvent(page: number, accumulate: boolean): PageEvent {
    const { queryKey } = this.props;
    const { query } = this.state;

    return new PageEvent({
      accumulate,
      limit: this.getLimit(),
      page,
      params: {
        [queryKey]: query,
      },
    });
  }

  renderContent(): ReactElement {
    const { children, data, done, emptyResultExpression, loading, localData, primaryKey, uuid } = this.props;
    const { internalValue } = this.state;

    const selected = internalValue ? [internalValue] : [];

    const scrollParent = this.modalRef.current
      ? this.modalRef.current.getModalElement()
      : null;

    return (
      <InfiniteScroll
        className="c-autocomplete__content"
        getScrollParent={() => scrollParent}
        hasMore={localData ? !done : false}
        initialLoad={localData}
        loadMore={this.onLoadMore}
        pageStart={0}
        threshold={100}
        useWindow={false}
      >
        <SelectableIterator
          primaryKey={primaryKey}
          onSelect={this.onItemSelected}
          loading={Boolean(loading)}
          emptyResultExpression={emptyResultExpression}
          done={localData || Boolean(done)}
          data={data}
          selected={selected}
          uuid={uuid}
        >
          {children}
        </SelectableIterator>
      </InfiniteScroll>
    );
  }

  renderClearAction = (): ReactElement | null => {
    const { enabled = true, readOnly } = this.props;
    const { internalValue, open } = this.state;

    if (!enabled || readOnly || open || !internalValue) {
      return null;
    }

    return (
      <button type="button" className="i-clear" onClick={this.onClearClick} />
    );
  };

  getInputValue = (): string => {
    const { focused, open, query } = this.state;

    if (open || focused) {
      return query;
    }

    return this.getDescriptionValue();
  };

  renderInput(): ReactElement {
    const { enabled, placeholder, readOnly } = this.props;

    return (
      <div className="c-autocomplete__search">
        <Input
          autoComplete="off"
          autoCorrect={false}
          enabled={enabled}
          externalAction={this.renderClearAction()}
          onBlur={this.onInputBlur}
          onChange={this.onInputChange}
          onFocus={this.onInputFocus}
          placeholder={placeholder}
          readOnly={readOnly}
          ref={this.inputRef}
          value={this.getInputValue()}
        />
      </div>
    );
  }

  renderMobile(): ReactElement {
    const { enabled, placeholder, readOnly, title } = this.props;
    const { open, query } = this.state;

    return (
      <div className={this.getClassName()}>
        <FakeInput
          enabled={enabled}
          externalAction={this.renderClearAction()}
          onFocus={() => this.onInputFocus(false)}
          placeholder={placeholder}
          readOnly={readOnly}
          value={this.getDescriptionValue()}
        />
        {open && !readOnly && (
          <Modal
            className="c-autocomplete__modal"
            focusOnOpen={false}
            onClose={this.onClose}
            ref={this.modalRef}
            title={title || placeholder}
            type={ModalType.FULLSCREEN}
          >
            <ModalInput
              onBlur={this.onInputBlur}
              onChange={this.onInputChange}
              onFocus={this.onModalInputFocus}
              placeholder={placeholder}
              value={query}
            />
            {this.renderContent()}
          </Modal>
        )}
      </div>
    );
  }

  render(): ReactElement | null {
    if (isMobile()) {
      return this.renderMobile();
    }

    const { readOnly, uuid } = this.props;
    const { open } = this.state;

    const Container = ContainerProvider.provide(this);

    if (!Container || !uuid) {
      return null;
    }

    return (
      <Container acceptedTypes={acceptedTypes} className={this.getClassName()} uuid={uuid}>
        <Popover
          alignment={Alignment.LEFT}
          matchTargetWidth
          maxHeight={320}
          onClose={this.onClose}
          open={open && !readOnly}
          showArrow={false}
        >
          {this.renderInput()}

          <Popover.Content>
            {this.renderContent()}
          </Popover.Content>

        </Popover>
      </Container>
    );
  }
}

export default Autocomplete;
